import React from 'react';

import { ChevronRight } from 'lucide-react';

import { cn } from '../../lib/utils';

import { SelectionIndicator } from './PeoplePickerPanel';

/**
 * Settings modal building blocks (see WorkspaceOptionsModal for the reference layout).
 * This is the canonical structure for settings-style modals going forward:
 *
 *   <SettingsGroup>
 *     <SettingsRow label="Notifications" value="All messages" onClick={...} />
 *     <SettingsRow label="Custom Emojis" chevron onClick={...} />
 *   </SettingsGroup>
 *   <SettingsGroup>
 *     <SettingsRow label="Leave" destructive onClick={...} />
 *   </SettingsGroup>
 *
 * Row conventions: `value` shows current state on the right (string or node, e.g. a
 * ProfileTag). `chevron` marks a bare navigation row — rows with a value skip the
 * chevron on desktop. `destructive` renders the label in red.
 */

// Dividers vanish next to the hovered row (same sibling-selector trick as the workspace rail)
// so the row's white hover fill gets clean edges. transform-gpu + will-change keep Safari
// repainting these hairline strips inside a scrolled modal.
const SETTINGS_DIVIDER_CLASS =
  'h-[0.5px] w-[calc(100%-32px)] shrink-0 self-center bg-gray-200 transition-opacity duration-300 ease-fluid transform-gpu will-change-[opacity] dark:bg-neutral-800 [:hover+&]:opacity-0 [&:has(+:hover)]:opacity-0';

export const SettingsGroup = ({ children, className }) => {
  const items = React.Children.toArray(children).filter(Boolean);
  if (!items.length) return null;

  return (
    <div
      className={cn(
        'flex w-full flex-col overflow-clip rounded-lg border-[0.5px] border-white bg-gray-50 dark:border-neutral-800 dark:bg-neutral-900',
        className,
      )}
    >
      {items.map((child, index) => (
        <React.Fragment key={child.key ?? index}>
          {index > 0 && <div className={SETTINGS_DIVIDER_CLASS} />}
          {child}
        </React.Fragment>
      ))}
    </div>
  );
};

export const SettingsRow = ({ label, value, icon: Icon = null, chevron = false, selected = false, destructive = false, disabled = false, onClick }) => {
  return (
    <button
      type="button"
      data-app-region="none"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'flex w-full cursor-pointer items-center justify-between gap-3 px-4 py-4 text-left text-cursed font-normal hover:bg-white disabled:cursor-not-allowed disabled:opacity-50 dark:hover:bg-neutral-800',
        destructive ? 'text-red-500' : 'text-black dark:text-neutral-100',
      )}
    >
      {/* Same icon recipe as the context menus: lucide at 12px (default stroke = absolute 1px), 10px from the label */}
      <span className="flex min-w-0 flex-1 items-center gap-2.5">
        {Icon && <Icon size={12} aria-hidden="true" className="shrink-0" />}
        <span className="min-w-0 truncate">{label}</span>
      </span>
      {value && <span className="flex shrink-0 items-center text-cursed text-gray-500 dark:text-neutral-500">{value}</span>}
      {!value && selected && <SelectionIndicator checked />}
      {!value && !selected && chevron && (
        <ChevronRight className="size-4.5 shrink-0 text-black dark:text-neutral-100" strokeWidth={1} absoluteStrokeWidth />
      )}
    </button>
  );
};

export const SettingsCheckbox = ({ label, checked, disabled = false, onChange }) => {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={checked}
      data-app-region="none"
      onClick={() => onChange(!checked)}
      disabled={disabled}
      className="group flex w-full cursor-pointer items-center justify-between gap-3 px-4 py-4 text-left text-cursed font-normal text-black hover:bg-white disabled:cursor-not-allowed disabled:opacity-50 dark:text-neutral-100 dark:hover:bg-neutral-800"
    >
      <span className="min-w-0 flex-1 truncate">{label}</span>
      <SelectionIndicator checked={checked} />
    </button>
  );
};

// A row whose right side is an on/off switch. `onChange` receives the next value.
// The whole row is the hit target, matching SettingsRow. `description` renders as a
// smaller gray line under the label.
export const SettingsToggle = ({ label, description, checked, disabled = false, onChange }) => {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      data-app-region="none"
      onClick={() => onChange(!checked)}
      disabled={disabled}
      className="flex w-full cursor-pointer items-center justify-between gap-3 px-4 py-4 text-left text-cursed font-normal text-black hover:bg-white disabled:cursor-not-allowed disabled:opacity-50 dark:text-neutral-100 dark:hover:bg-neutral-800"
    >
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="truncate">{label}</span>
        {description && <span className="truncate text-[13px] leading-5 text-gray-500 dark:text-neutral-500">{description}</span>}
      </span>
      <span
        className={cn(
          'relative h-6 w-10 shrink-0 rounded-full transition-colors duration-150 ease-fluid',
          checked ? 'bg-idle-blue' : 'bg-gray-300 dark:bg-neutral-700',
        )}
      >
        <span
          className={cn(
            'absolute top-0.5 left-0.5 size-5 rounded-full bg-white shadow-sm transition-transform duration-150 ease-fluid',
            checked && 'translate-x-4',
          )}
        />
      </span>
    </button>
  );
};

export const SettingsQuickAction = ({ icon: Icon, label, onClick, disabled = false }) => {
  return (
    <button
      type="button"
      data-app-region="none"
      onClick={onClick}
      disabled={disabled}
      className="flex h-20 w-24 cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border-[0.5px] border-white bg-gray-50 text-cursed text-black hover:bg-white disabled:cursor-not-allowed disabled:opacity-50 dark:border-neutral-800 dark:bg-neutral-900 dark:text-neutral-100 dark:hover:bg-neutral-800"
    >
      <span className="flex size-6 items-center justify-center">
        <Icon className="size-4.5" strokeWidth={1.5} absoluteStrokeWidth />
      </span>
      <span>{label}</span>
    </button>
  );
};
